/* eslint-disable max-len */
/* eslint-disable security/detect-object-injection */

import React, { useContext, useMemo, useState } from 'react';
import GlobalContext from '../Context/GloablContext';
import UpdateButton from '../UpdateButton';
import { useNavigate } from 'react-router';
import WarningMessage from '../WarningMessage';
import Toast from '../Toast';
import { ConvertToTitleCase } from '../../CommonMethods/ConvertToTitleCase';
import CardHeader from '../CardHeader';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableAddRow = ({ id, index, name, renderRow }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const dragHandle = (
        <div className="w-[7%] flex justify-center">
            <button
                type="button"
                className="bulk-add-drag-handle"
                {...attributes}
                {...listeners}
                aria-label={`Reorder ${name} row ${index + 1}`}
            >
                <span></span>
                <span></span>
                <span></span>
            </button>
        </div>
    );

    return renderRow(dragHandle, setNodeRef, style);
};

const AddAndRemoveSection = ({
    newClassFields,
    setNewClassFields,
    Tittle,
    handleAdd,
    cancelUrl,
    existingName,
    setExistingName,
    toastErrorMessage,
    setToastErrorMessage,
    loading,
    type,
    Name,
    existingItems = [],
    existingItemsLoading = false,
    insertEntityName = '',
    insertType = 'END',
    setInsertType = () => {},
    afterItemId = '',
    setAfterItemId = () => {},
}) => {
    const {isSideNavBar, swithBranch} = useContext(GlobalContext);
    const [selectedInput, setSelectedInput] = useState();
    const [warningMessage, setWarningMessage] = useState('')
    const [emptyField, setEmptyField] = useState(false);
    const [selectedDescription, setSelectedDescription] = useState(null)
    const navigate = useNavigate();
    const isOrderedBulkAdd = type === 'Class' || type === 'Section';
    const insertEntityLabel = insertEntityName == 'Class' ? 'classes' : 'sections';
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 8,
        },
    }));

    const sortedExistingItems = useMemo(
        () => [...(existingItems || [])].sort(
            (a, b) => Number(a?.display_order || 0) - Number(b?.display_order || 0)
        ),
        [existingItems]
    );
    const shouldShowInsertControls = isOrderedBulkAdd && sortedExistingItems.length > 0;
    const shouldReserveInsertControlsSpace = isOrderedBulkAdd && existingItemsLoading;
    const existingItemNames = useMemo(
        () => new Set(
            sortedExistingItems
                .map((item) => String(item?.name || item?.section || item?.section_name || '').trim().toLowerCase())
                .filter(Boolean)
        ),
        [sortedExistingItems]
    );

    const sanitizeRows = (rows = []) => rows
        .map((item, index) => ({
            ...item,
            _rowId: item?._rowId || `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
            name: String(item?.name || '').trim(),
            description: item?.description || '',
        }))
        .filter((item) => item.name !== '');

    const getDuplicateNames = (rows = []) => {
        const namesCount = rows.reduce((count, obj) => {
            const trimmedName = String(obj?.name || '').trim().toLowerCase();
            if (!trimmedName) return count;
            count[trimmedName] = (count[trimmedName] || 0) + 1;
            return count;
        }, {});
        return Object.keys(namesCount).filter((name) => namesCount[name] > 1);
    };

    const getExistingNameConflicts = (rows = []) => rows
        .map((item) => String(item?.name || '').trim().toLowerCase())
        .filter((name, index, arr) => name && arr.indexOf(name) === index && existingItemNames.has(name));

    const handleAddClassField = () => {
        setEmptyField(false);
        setToastErrorMessage('')
        let newFields = type === "subject" ? { name: '', description: '', is_optional : false} :{ name: '', description: '' }
        const updatedClassFields = [
            ...newClassFields,
            { ...newFields, _rowId: `${Date.now()}-${newClassFields.length}` },
        ];
        setNewClassFields(updatedClassFields);

        if (newClassFields?.length === 19)  {
            setToastErrorMessage('')
            setWarningMessage(Tittle)
        }
    };

    const handleRemoveClassField = (indexToRemove) => {
        setEmptyField(false);
        setToastErrorMessage('')
        const updatedClassFields = newClassFields.filter((_, index) => index !== indexToRemove);
        setNewClassFields(updatedClassFields);
    };

    const handleClassFieldChange = (index, field, value) => {
        setToastErrorMessage('');
        setEmptyField(false);
        setExistingName([]);
        setSelectedDescription(null);
        const updatedClassFields = [...newClassFields];
        if(field === 'description'){
            const words = value?.split(" ");
            if (words.length <= 100) {
                updatedClassFields[index][field] = field === "description" ? value : value;
            }
        }else{
            updatedClassFields[index][field] = field === "description" ? value : value;
        }
        if(field === 'description') {
            const words = value?.split(" ");
            if(words?.length > 100) {
                setSelectedDescription(index)
                setWarningMessage('')
                setToastErrorMessage('Description cannot exceed 100 words'); 
            }
        }
        setNewClassFields(updatedClassFields);
    };

    const handleAddClass = () => {
        setSelectedInput(null);
        setEmptyField(false);
        const sanitizedRows = sanitizeRows(newClassFields);
        const duplicateNames = getDuplicateNames(sanitizedRows);
        const existingItemConflicts = isOrderedBulkAdd ? getExistingNameConflicts(sanitizedRows) : [];

        setNewClassFields(sanitizedRows.length > 0 ? sanitizedRows : [{
            ...(type === "subject" ? { name: '', description: '', is_optional : false } : { name: '', description: '' }),
            _rowId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        }]);
        setExistingName([...duplicateNames, ...existingItemConflicts]);

        if (sanitizedRows.length === 0) {
            setEmptyField(true);
            setWarningMessage('')
            setToastErrorMessage('Please fill in all the mandatory fields');
            return;
        } else if(duplicateNames?.length > 0) {
            setWarningMessage('')
            setToastErrorMessage(`Duplicate Entry: ${Name} Name already added`);  
            return;
        } else if (existingItemConflicts?.length > 0) {
            setWarningMessage('');
            setToastErrorMessage(`Duplicate Entry: ${Name} name already exists`);
            return;
        } else {
            handleAdd({
                items: sanitizedRows,
                insertType: isOrderedBulkAdd ? insertType : undefined,
                afterItemId: isOrderedBulkAdd && insertType === 'AFTER' ? afterItemId : undefined,
            })
        }
    };

    const onBlur = event => {
        setSelectedInput(null);
        setEmptyField(false);
        const sanitizedRows = sanitizeRows(newClassFields);
        const duplicateNames = getDuplicateNames(sanitizedRows).map((name) => name.trim());
        const existingItemConflicts = isOrderedBulkAdd ? getExistingNameConflicts(sanitizedRows) : [];
        setExistingName([...duplicateNames, ...existingItemConflicts]);
        if (duplicateNames?.length > 0) {
            setWarningMessage('')
            setToastErrorMessage(`Duplicate Entry: ${Name} Name already added`);
            return;
        }
        if (existingItemConflicts?.length > 0) {
            setWarningMessage('');
            setToastErrorMessage(`Duplicate Entry: ${Name} name already exists`);
        }
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!active?.id || !over?.id || active.id === over.id) return;

        const oldIndex = newClassFields.findIndex((item) => item?._rowId === active.id);
        const newIndex = newClassFields.findIndex((item) => item?._rowId === over.id);

        if (oldIndex < 0 || newIndex < 0) return;
        setNewClassFields(arrayMove(newClassFields, oldIndex, newIndex));
    };

    const renderRowContent = (field, index, dragHandle = null, rowRef = null, rowStyle = undefined) => (
        <div
            ref={rowRef}
            style={rowStyle}
            className="flex items-center p-2 border-t border-[#E6E8EA] w-[100%]"
        >
            {dragHandle}
            <div className={`flex items-center ${isOrderedBulkAdd ? 'w-[30%]' : 'w-[35%]'}`}>
                <label htmlFor={`fname${index}`}></label>
                <input
                    type="text"
                    onBlur={onBlur}
                    autoComplete='off'
                    id={`fname${index}`}
                    maxLength={30}
                    className={`inpt-fld ${((emptyField && field.name.trim() === '') || (existingName?.includes(field.name.trim())) || (existingName?.includes(field.name.trim().toLowerCase()) && selectedInput !== index))? "redBorder" : ""} w-[200px] `}
                    placeholder={`Enter ${Name} Name`}
                    value={field.name}
                    onChange={(e) => {setSelectedInput(index); handleClassFieldChange(index, 'name', e.target.value)}}
                />
            </div>
            <div className={`${isOrderedBulkAdd ? 'w-[55%]' : 'w-[50%]'} flex items-center`}>
                <div className={`red-border1 rounded ${type === "subject"?'w-[65%]':'w-[95%]'}`}>
                    <textarea
                        id={`Description${index}`}
                        className={`txt-fld w-full ${selectedDescription === index ? 'redBorder' : ''}`}
                        placeholder="Enter Description"
                        style={{'height':'43px'}}
                        value={field.description}
                        autoComplete='off'
                        maxLength='100'
                        onChange={(e) =>
                        {
                            setSelectedInput(null);
                            handleClassFieldChange(
                                index,
                                'description',
                                e.target.value
                            )}
                        }
                    ></textarea>
                </div>
                {type === "subject" && <div className='pl-6 w-[43%]'>
                    <div class="relative custom-radioBlue flex mb-1" >
                        <input id={'mandatory'+index} type="radio" name={'mandatory'+index} onChange={() => handleClassFieldChange(index,'is_optional', false)}
                            checked={!field.is_optional}/>
                        <label for={'mandatory'+index} >Mandatory</label>
                    </div>
                    <div class="relative custom-radioBlue flex">
                        <input id={'optional'+index} type="radio" name={'mandatory'+index} onChange={() => handleClassFieldChange(index,'is_optional', true)}
                            checked={field.is_optional}/>
                        <label for={'optional'+index} >Optional</label>
                    </div>
                </div>}
            </div>
            <div className="w-[10%] flex items-center">
                {index === newClassFields?.length - 1 ? (
                    <>
                        {index !== 0 && <div className='group relative'>
                            <img
                                src="/images/delete-bg-red-icon.svg"
                                alt="icon"
                                className="ml-4 cursor-pointer delete-icon"
                                onClick={() => handleRemoveClassField(index)}
                            />
                            <span class="absolute top-8 -left-[3px] scale-0 inline-flex flex-shrink-0 justify-center items-center gap-2.5 p-2.5 h-5 rounded-sm bg-[#d0d5dd] text-[#344054] text-xs font-medium leading-[12.5px] group-hover:scale-100">Delete</span>
                        </div>}
                        {index !== 19 &&<div className='group relative'>
                            <img
                                src="/images/add-icon-blue.svg"
                                id = "plus_iconn"
                                alt="icon"
                                className="ml-4 cursor-pointer add-icon"
                                onClick={handleAddClassField} />
                            <span class="absolute top-8 left-[5px] scale-0 inline-flex flex-shrink-0 justify-center items-center gap-2.5 p-2.5 h-5 rounded-sm bg-[#d0d5dd] text-[#344054] text-xs font-medium leading-[12.5px] group-hover:scale-100">Add</span>
                        </div>}
                    </>
                ) : (
                    <div className='group relative'>
                        <img
                            src="/images/delete-bg-red-icon.svg"
                            alt="icon"
                            className="ml-4 cursor-pointer delete-icon"
                            onClick={() => handleRemoveClassField(index)}
                        />
                        <span class="absolute top-8 -left-[3px] scale-0 inline-flex flex-shrink-0 justify-center items-center gap-2.5 p-2.5 h-5 rounded-sm bg-[#d0d5dd] text-[#344054] text-xs font-medium leading-[12.5px] group-hover:scale-100">Delete</span>

                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            <CardHeader noPadding>
                <div class="mainDataCntnrTop shadow-s13" style={{position: "sticky", position: "-webkit-sticky"}}>
                    <h5 class="pageHeader addclass">Add {Tittle}</h5>
                    <div class="flex items-center">
                        <UpdateButton
                            ButtonName={"Cancel"}
                            loading={false}
                            handleOnCLick={() => navigate(cancelUrl)}
                            classKey="cancl-btn"
                        />
                        <UpdateButton 
                            loading={loading}
                            ButtonName={"Add"}
                            handleOnCLick={handleAddClass}
                            classKey={"actn-btn onboardInstitution"}
                        />
                    </div>
                    {toastErrorMessage!=='' &&<Toast
                        message={toastErrorMessage}
                        type="error"
                        setToastmessage={setToastErrorMessage} />}
                    {warningMessage !== "" && 
                    <WarningMessage
                        warningMessage={warningMessage}
                        setWarningMessage={setWarningMessage}
                    />}
                </div>
                <div className='mainDataCntnrPageDiv scrollBlue_thick'> 
                    <div className=''>
                        {shouldReserveInsertControlsSpace && (
                            <div className="p-3 border rounded border-[#E6E8EA] mb-2 mx-auto w-[65%] 2xl:w-[58%] min-h-[64px] bg-[#F8FAFC]"></div>
                        )}
                        {shouldShowInsertControls && (
                            <div className="p-[14px] border rounded border-[#E6E8EA] mb-4 mx-auto w-[65%] 2xl:w-[58%] flex">
                                <p className="w-[25%] mt-1">{`Insert ${insertEntityLabel} at`}</p>
                                <div className="flex items-center gap-12 flex-wrap">
                                    <label className="flex items-center gap-2 text-sm text-[#031624]">
                                        <input
                                            type="radio"
                                            name="insertClassPosition"
                                            checked={insertType === 'END'}
                                            onChange={() => setInsertType('END')}
                                        />
                                        End
                                    </label>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <label className="flex items-center gap-2 text-sm text-[#031624]">
                                            <input
                                                type="radio"
                                                name="insertClassPosition"
                                                checked={insertType === 'AFTER'}
                                                onChange={() => setInsertType('AFTER')}
                                            />
                                            After
                                        </label>
                                        <select
                                            className="inpt-fld w-[240px]"
                                            value={afterItemId}
                                            disabled={insertType !== 'AFTER'}
                                            onChange={(e) => setAfterItemId(e.target.value)}
                                        >
                                            {sortedExistingItems.map((item) => (
                                                <option key={item?._id} value={item?._id}>
                                                    {item?.name || item?.section || item?.section_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="mx-auto w-[65%] 2xl:w-[58%] border border-[#E6E8EA]">
                            
                            <div class="flex items-center pb-3 p-2 formHeaderbg">
                                {isOrderedBulkAdd && <p className="w-[7%]"></p>}
                                <p class={`font-semibold text-f7 text-[#031624] class-name-error ${isOrderedBulkAdd ? 'w-[30%]' : 'w-[35%]'}`}>{ConvertToTitleCase(type)} Name</p>
                                <p class={`font-semibold text-f7 text-[#031624] descri-error ${isOrderedBulkAdd ? 'w-[55%]' : 'w-[50%]'}`}>Description</p>
                            </div>
                            <div className="rounded">
                                {isOrderedBulkAdd ? (
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <SortableContext
                                            items={newClassFields.map((field, index) => field?._rowId || `${index}-row`)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {newClassFields.map((field, index) => (
                                                <SortableAddRow
                                                    key={field?._rowId || `${index}class`}
                                                    id={field?._rowId || `${index}-row`}
                                                    index={index}
                                                    name={Name}
                                                    renderRow={(dragHandle, rowRef, rowStyle) =>
                                                        renderRowContent(field, index, dragHandle, rowRef, rowStyle)
                                                    }
                                                />
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                ) : (
                                    newClassFields.map((field, index) => (
                                        <React.Fragment key={field?._rowId || `${index}class`}>
                                            {renderRowContent(field, index)}
                                        </React.Fragment>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                  
            </CardHeader>
        </>
    );
};

export default AddAndRemoveSection;
